import { DepartmentColumns, HanRoleColumns, JobTitleColumns } from '@/backend/config/classifiers';
import { AppContext } from '@/ui/components/Context';
import { useEnvironmentConfig } from '@/ui/hooks/useEnvironmentConfig';
import { appName } from '@/ui/utils/utils';
import { buildAlertsValueProviders } from '@juvare/alert-ui/ui/features/forms/utils';
import { buildCallIntervalValueProvider } from '@juvare/alert-ui/ui/features/forms/utils/CallIntervalValueProvider';
import { buildMessageTypeValueProvider } from '@juvare/alert-ui/ui/features/forms/utils/MessageTypeValueProvider';
import { buildRepeatCallValueProvider } from '@juvare/alert-ui/ui/features/forms/utils/RepeatCallValueProvider';
import { FormConfigContext } from '@juvare/common-form-ui/FormConfigContext';
import { defaultFormWidgets } from '@juvare/common-form-ui/WidgetForm';
import { GlobalLoader } from '@juvare/common-web-ui/GlobalLoader';
import {
    contactManagementFormWidget,
    getContactManagementFormConfigRules,
    useBuildContactManagementFormValueProviders,
} from '@juvare/contact-management/ui/features/forms';
import { useContactManagementTranslation } from '@juvare/contact-management/ui/hooks/useContactManagementTranslation';
import { getOccupationFormConfigFunctionDef } from '@juvare/credentialing/backend/config/shared/platform-function-types';
import {
    buildCredentialingSubFormProviders,
    buildCredentialingValueProviders,
    createCredentialingFormRules,
} from '@juvare/credentialing/ui/features/forms';
import { useCredentialingTranslation } from '@juvare/credentialing/ui/hooks';
import { Settings } from '@juvare/platform-sdk-react/types';
import { mapAdvancedSearchOpToStringColumnFilter } from '@juvare/platform-sdk-react/ui/features/advanced-search';
import { useConfigurableListsAsValueProvider } from '@juvare/platform-sdk-react/ui/features/configurable-lists';
import {
    useCountyValueProvider,
    useRegionValueProvider,
    useStateAndTerritoryValueProvider,
    useStateValueProvider,
    useTerritoryValueProvider,
} from '@juvare/platform-sdk-react/ui/features/default-classifiers';
import {
    DynamicColumnConfigProvider,
    DynamicColumnHandler,
    getCountyColumnHandler,
    getRegionColumnHandler,
    getStateColumnHandler,
    getStateTerritoryColumnHandler,
    getTerritoryColumnHandler,
} from '@juvare/platform-sdk-react/ui/features/dynamic-columns';
import { getColumnsHandlerTypeId } from '@juvare/platform-sdk-react/ui/features/dynamic-columns/utils/getColumnsHandlerTypeId';
import { getColumnsHandlerTypeString } from '@juvare/platform-sdk-react/ui/features/dynamic-columns/utils/getColumnsHandlerTypeString';
import { getPublicSettings, getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { isUserLoggedIn } from '@juvare/platform-sdk-react/ui/stores';
import React, { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getHanFormConfigRules } from '../hanFormConfigRules';
import {
    buildSubCategoryValueProvider,
    useCategoryValueProvider,
    useDepartmentValueProvider,
    useHanRolesValueProvider,
    useJobTitleValueProvider,
} from '../utils';

export const FormConfigContextProvider: React.FC<PropsWithChildren<{ googleRecaptchaV2SiteKey?: string }>> = ({
    googleRecaptchaV2SiteKey,
    children,
}) => {
    const appContext = React.useContext(AppContext);
    const { data: environmentConfig } = useEnvironmentConfig();
    const userLoggedIn = useSelector(isUserLoggedIn);
    const settings = useSelector(userLoggedIn ? getSettings : getPublicSettings);
    const userState = appContext.userState;
    const { t } = useContactManagementTranslation();
    const { t: t1 } = useTranslation<'healthAlerts'>();
    const callIntervalValueProvider = buildCallIntervalValueProvider(appName);
    const configurableLists = useConfigurableListsAsValueProvider();
    const countyValueProvider = useCountyValueProvider();
    const departmentValueProvider = useDepartmentValueProvider();
    const hanRolesValueProvider = useHanRolesValueProvider();
    const jobTitleValueProvider = useJobTitleValueProvider();
    const messageTypeValueProvider = buildMessageTypeValueProvider(appName);
    const pcrCategoriesFormValueProvider = useCategoryValueProvider(settings);
    const pcrSubCategoriesFormValueProvider = buildSubCategoryValueProvider(settings);
    const regionValueProvider = useRegionValueProvider();
    const repeatCallValueProvider = buildRepeatCallValueProvider(appName);
    const stateTerritoryValueProvider = useStateAndTerritoryValueProvider();
    const stateValueProvider = useStateValueProvider();
    const territoryValueProvider = useTerritoryValueProvider();

    const getOccupationFormConfig = useTypedPlatformFunction(getOccupationFormConfigFunctionDef);
    const { t: credentialingTranslations } = useCredentialingTranslation();

    const contactManagementFormValueProviders = useBuildContactManagementFormValueProviders(
        t,
        settings as Settings,
        appContext.userState,
        appName,
        [],
    );

    if (configurableLists === undefined) {
        return <GlobalLoader />;
    }

    const handlers: DynamicColumnHandler[] = [
        getCountyColumnHandler(countyValueProvider, environmentConfig?.stateCode),
        getStateColumnHandler(stateValueProvider),
        getStateTerritoryColumnHandler(stateTerritoryValueProvider),
        getTerritoryColumnHandler(territoryValueProvider),
        getRegionColumnHandler(regionValueProvider),
        getColumnsHandlerTypeId(hanRolesValueProvider, {
            subFilterEntity: 'hanRole',
            onGetStorageSubFilter: (operator, value) => {
                return mapAdvancedSearchOpToStringColumnFilter<HanRoleColumns>('name', operator, value);
            },
        }),
        getColumnsHandlerTypeId(jobTitleValueProvider, {
            subFilterEntity: 'jobTitle',
            onGetStorageSubFilter: (operator, value) => {
                return mapAdvancedSearchOpToStringColumnFilter<JobTitleColumns>('name', operator, value);
            },
        }),
        getColumnsHandlerTypeId(departmentValueProvider, {
            subFilterEntity: 'department',
            onGetStorageSubFilter: (operator, value) => {
                return mapAdvancedSearchOpToStringColumnFilter<DepartmentColumns>('name', operator, value);
            },
        }),
    ];

    for (const provider of configurableLists) {
        handlers.push(getColumnsHandlerTypeString(provider));
    }

    return (
        <FormConfigContext.Provider
            value={{
                widgets: [...defaultFormWidgets, ...contactManagementFormWidget],
                valueProviders: [
                    stateValueProvider,
                    countyValueProvider,
                    hanRolesValueProvider,
                    jobTitleValueProvider,
                    departmentValueProvider,
                    stateTerritoryValueProvider,
                    territoryValueProvider,
                    regionValueProvider,
                    messageTypeValueProvider,
                    callIntervalValueProvider,
                    repeatCallValueProvider,
                    pcrCategoriesFormValueProvider,
                    pcrSubCategoriesFormValueProvider,
                    ...contactManagementFormValueProviders,
                    ...configurableLists,
                    ...buildCredentialingValueProviders(appName, appContext.userState),
                    ...buildAlertsValueProviders(appName),
                ],
                subFormProviders: buildCredentialingSubFormProviders(
                    appName,
                    credentialingTranslations,
                    getOccupationFormConfig,
                    userState,
                ),
                rules: [
                    ...getContactManagementFormConfigRules(t, settings as Settings, appContext.userState),
                    ...getHanFormConfigRules(t1, appContext.userState),
                    ...createCredentialingFormRules(credentialingTranslations, settings, userState),
                ],
                globalWidgetConfig: { googleRecaptchaV2SiteKey },
            }}
        >
            <DynamicColumnConfigProvider columnHandlers={(defaultHandlers) => [...defaultHandlers, ...handlers]}>
                {children}
            </DynamicColumnConfigProvider>
        </FormConfigContext.Provider>
    );
};
